import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import loginImage from '../../assets/images/login.png';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { AUTH_STACK } from '../../utils/screenNames';
import { forgotPassword } from '../../redux/actions/authActions';
import { passwordResetRequested } from '../../utils/analytics';

/**
 * ForgotPasswordWithEmail Page - Refactored with Tailwind CSS
 * Sends password reset code to user's email
 */
function ForgotPasswordWithEmail() {
  const navigate = useNavigate();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateEmail,
    validate,
    setError,
  } = useAuthForm({ email: '' });

  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle password reset request
   */
  const handleResetPassword = async () => {
    // Validate email
    const isValid = validate({
      email: validateEmail,
    });

    if (!isValid) return;

    setIsLoading(true);

    try {
      await forgotPassword(
        values.email,
        () => {
          passwordResetRequested({ method: 'email' });
          setIsLoading(false);
          navigate(AUTH_STACK.RESET_PASSWORD_WITH_EMAIL, {
            state: { email: values.email },
          });
        },
        () => {
          setIsLoading(false);
          setError('email', 'Failed to send reset code. Please try again.');
        }
      );
    } catch (error) {
      setIsLoading(false);
      setError('email', 'An error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-app-bg">
      {/* Back Button */}
      {/* <button
        onClick={() => navigate(AUTH_STACK.LOGIN_OPTIONS)}
        className="absolute top-4 left-4 md:top-6 md:left-6 z-20 flex items-center gap-2 text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back to login</span>
      </button> */}

      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Reset password"
        subtitle="Enter your email and we'll send you a reset link."
      >
        {/* Email Input */}
        <div className="w-full">
          <Input
            type="email"
            name="email"
            placeholder="Enter your email here"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            errorMessage={errors.email}
          />
        </div>

        {/* Send Code Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleResetPassword}
          className="w-full h-11 bg-[#00E7FF] mt-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          disabled={!values.email}
        >
          Send a code
        </Button>

        {/* Back to Login Link */}
        <button
          onClick={() => navigate(AUTH_STACK.LOGIN_OPTIONS)}
          className="w-full text-center text-sm text-app-text-secondary hover:text-light-blue transition-colors font-proxima mt-4"
        >
          Back to login
        </button>
      </AuthLayout>
    </div>
  );
}

export default ForgotPasswordWithEmail;
